import { DeleteTwoTone, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Empty, Flex, Image, Popconfirm, Typography, UploadFile } from 'antd';
import { Fragment, useContext, useMemo, useState } from 'react';
import {
  deleteTransportationTicketFile,
  updateTransportationTicketFile,
  uploadTransportationTicketFile
} from 'src/apis/hr_ticket/transportationTicket.api';
import AttachmentUploadModal from 'src/components/v2/Upload/AttachmentUpload';
import urlPath from 'src/constants/urlPath';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { TicketStatus } from 'src/types/helpdesk/ticket.type';
import {
  ITransportationAttachFile,
  ITransportationAttachFileCreateDto
} from 'src/types/hr_ticket/transportationTicket.type';
import { showNotification } from 'src/utils/notification';
import { getPathsFromRoles } from 'src/utils/utils';
import './TransportationAttachment.scss';
import TransportationAttachmentImage from './TransportationAttachmentImage';
import { DepartmentEn } from 'src/constants/department.enum';
const { Text } = Typography;

export default function TransportationAttachment() {
  const { transportationTicket } = useContext(HrTicketDetailContext);
  const { userInfo, roles } = useContext(AppContext);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState<boolean>(false);
  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const pathsFromRole = useMemo(() => {
    return getPathsFromRoles(roles);
  }, [roles]);

  const isShowUploadBtn =
    ((basePath === urlPath.myTransportationTicket.location &&
      transportationTicket?.data.createdById === userInfo?.id) ||
      (pathsFromRole.includes(urlPath.hrConfirmTransportationTicket.location) &&
        userInfo?.departmentId === DepartmentEn.hr)) &&
    transportationTicket?.data.ticketStatusId === TicketStatus.ApprovedRequest &&
    transportationTicket?.data.confirmedType === ConfirmTypeEn.WaitingConfirm;

  const updateTransportationTicketAttacheMutation = useMutation({
    mutationFn: (body: ITransportationAttachFileCreateDto[]) =>
      updateTransportationTicketFile(transportationTicket?.data.id, body)
  });
  const deleteTransportationTicketFileMutation = useMutation({
    mutationFn: (path: string) => deleteTransportationTicketFile([path])
  });

  const uploadTransportationTicketFileMutation = useMutation({
    mutationFn: (body: FormData) => uploadTransportationTicketFile(body as FormData)
  });

  const handleDeleteAttache = (attache: ITransportationAttachFile) => {
    deleteTransportationTicketFileMutation.mutate((attache.path || attache.filePath) as string, {
      onSuccess: () => {
        const dataClone = { ...transportationTicket?.data };
        const newAttachments = (dataClone.transportationTicketAttachments || [])
          .filter((x) => x.id !== attache.id)
          .map((file) => ({
            path: file.path || file.filePath,
            name: file.name || file.originalName,
            thumbnailPath: '',
            fileType: file.fileType || file.contentType,
            size: 0
          }));

        updateTransportationTicketAttacheMutation.mutate(newAttachments, {
          onSuccess: () => {
            showNotification('Xoá tệp thành công!', 'success');
            queryClient.invalidateQueries(['transportationTicket']);
          }
        });
      }
    });
  };

  const handleSave = (fileList: UploadFile<File>[]) => {
    const formData = new FormData();
    fileList.length > 0 &&
      fileList.forEach(function (file) {
        formData.append('files', file.originFileObj as Blob);
      });

    return uploadTransportationTicketFileMutation.mutateAsync(formData, {
      onError: () => {
        alert('Lỗi ảnh');
      },
      onSuccess: (data) => {
        const newTransportationTicketAttachments = [
          ...(transportationTicket?.data?.transportationTicketAttachments || []),
          ...data.data
        ];

        const newAttachments = newTransportationTicketAttachments.map((file) => ({
          path: file.path || file.filePath,
          name: file.name || file.originalName,
          thumbnailPath: '',
          fileType: file.fileType || file.contentType,
          size: 0
        }));

        if (newAttachments?.length && newAttachments?.length > 0) {
          updateTransportationTicketAttacheMutation.mutate(newAttachments, {
            onSuccess: () => {
              setIsOpenUploadModal(false);
              showNotification('Thêm tệp thành công!', 'success');
              queryClient.invalidateQueries(['transportationTicket']);
            }
          });
        }
      }
    });
  };

  return (
    <Fragment>
      <Flex gap={10} vertical>
        <Divider style={{ marginBlock: 0 }} orientation='left'>
          <Text strong>Tệp đính kèm</Text>
        </Divider>
        {transportationTicket?.data.transportationTicketAttachments &&
        transportationTicket?.data.transportationTicketAttachments.length > 0 ? (
          <Image.PreviewGroup>
            <Flex wrap='wrap' gap={8} className='attache-list'>
              {transportationTicket?.data.transportationTicketAttachments.map((attache) => (
                <div className='attache-item' key={attache.id}>
                  <TransportationAttachmentImage
                    key={attache.id}
                    ticketAttachment={attache}
                    size={92}
                    className='attache-item-image'
                  />
                  {isShowUploadBtn && (
                    <Popconfirm title='Bạn chắc chắn muốn xoá tệp?' onConfirm={() => handleDeleteAttache(attache)}>
                      <Button size='small' type='default' className='attache-action'>
                        <DeleteTwoTone />
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              ))}
            </Flex>
          </Image.PreviewGroup>
        ) : (
          <Empty
            image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
            description={<Text type='secondary'>Không có tệp đính kèm</Text>}
          />
        )}
        {isShowUploadBtn && (
          <Button
            shape='default'
            type='link'
            size='middle'
            icon={<UploadOutlined />}
            onClick={() => setIsOpenUploadModal(true)}
          >
            Thêm đính kèm
          </Button>
        )}
      </Flex>
      {isOpenUploadModal && (
        <AttachmentUploadModal
          setIsOpen={setIsOpenUploadModal}
          isOpen={isOpenUploadModal}
          isLoading={uploadTransportationTicketFileMutation.isLoading}
          onSubmit={handleSave}
        />
      )}
    </Fragment>
  );
}
